import { useEffect, useMemo, useState, Fragment } from "react";
import { Search, Plus, Edit, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Save } from "lucide-react";
import { useSearchParams } from "react-router";
import {
  useCreateServiceCatalogOfferMutation,
  useGetServiceCatalogQuery,
  useGetServiceCodeMappingColumnsQuery,
  useGetServiceCodeMappingOptionsQuery,
  useGetServiceCodeMappingsQuery,
  useUpdateServiceCatalogOfferMutation,
  selectAuth,
  useAppSelector,
} from "../store";
import { hasOfferCatalogAddServiceButtonPrivilege } from "../../redux/utils/authPrivileges";

const PAGE_SIZE = 20;

const INITIAL_OFFER_FORM = {
  category: "",
  subCategory: "",
  serviceCode: "",
  description: "",
  glCode: "",
  unitOfMeasure: "",
  costSheetRate: "",
  sourceTable: "",
  fieldName: "",
  fieldName2: "",
  fieldName3: "",
  fieldName4: "",
  fieldName5: "",
};

const SERVICE_CODE_MAPPING_OPTIONAL_FIELD_COLUMNS = [
  { key: "fieldName2", label: "Field Name 2" },
  { key: "fieldName3", label: "Field Name 3" },
  { key: "fieldName4", label: "Field Name 4" },
  { key: "fieldName5", label: "Field Name 5" },
];

const MAPPING_FIELD_KEYS = ["fieldName", "fieldName2", "fieldName3", "fieldName4", "fieldName5"];

const MAPPING_FIELD_LABELS = {
  fieldName: "Field Name",
  fieldName2: "Field Name 2",
  fieldName3: "Field Name 3",
  fieldName4: "Field Name 4",
  fieldName5: "Field Name 5",
};

function mappingFieldOptions(allColumns, form, currentKey) {
  const usedBefore = new Set();
  for (const key of MAPPING_FIELD_KEYS) {
    if (key === currentKey) {
      break;
    }
    const value = String(form?.[key] ?? "").trim();
    if (value) {
      usedBefore.add(value);
    }
  }
  const columns = Array.isArray(allColumns) ? allColumns : [];
  const options = columns.filter((column) => !usedBefore.has(column));
  const current = String(form?.[currentKey] ?? "").trim();
  if (current && !options.includes(current)) {
    return [current, ...options];
  }
  return options;
}

function previousMappingFieldKey(key) {
  const index = MAPPING_FIELD_KEYS.indexOf(key);
  return index > 0 ? MAPPING_FIELD_KEYS[index - 1] : null;
}

function isFlatFeeCategory(category) {
  return String(category ?? "").trim().toLowerCase() === "flat fee";
}

function hasAnyServiceCodeMappingValues(form) {
  if (String(form?.sourceTable ?? "").trim()) {
    return true;
  }
  return MAPPING_FIELD_KEYS.some((key) => String(form?.[key] ?? "").trim());
}

function getVisibleServiceCodeMappingOptionalColumns(mappings) {
  return SERVICE_CODE_MAPPING_OPTIONAL_FIELD_COLUMNS.filter(({ key }) =>
    mappings.some((mapping) => Boolean(mapping[key]))
  );
}

function offerFieldClassName(hasError) {
  return `w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
    hasError
      ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
      : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
  }`;
}

function offerAddValueToggleClassName() {
  return "app-btn-plain inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20";
}

function catalogHasValue(options, value) {
  const target = String(value ?? "").trim().toLowerCase();
  if (!target) {
    return false;
  }
  return options.some((item) => String(item ?? "").trim().toLowerCase() === target);
}

function parseOfferCostSheetRate(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return { value: null, error: null };
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return { value: null, error: "Enter a valid cost sheet rate." };
  }
  if (parsed < 0) {
    return { value: null, error: "Cost sheet rate cannot be negative." };
  }
  return { value: parsed, error: null };
}

function validateOfferForm(
  form,
  {
    requireMapping,
    isAddingNewCategory,
    isAddingNewSubCategory,
    isAddingNewUnitOfMeasure,
    existingCategories = [],
    existingSubCategories = [],
    existingUnitsOfMeasure = [],
  } = {}
) {
  const errors = {};
  const category = String(form.category ?? "").trim();
  const subCategory = String(form.subCategory ?? "").trim();
  const unitOfMeasure = String(form.unitOfMeasure ?? "").trim();
  const serviceCode = String(form.serviceCode ?? "").trim();
  const description = String(form.description ?? "").trim();
  if (!category) {
    errors.category = "Category is required.";
  } else if (isAddingNewCategory && catalogHasValue(existingCategories, category)) {
    errors.category = "This category already exists. Select it from the list instead.";
  }
  if (isAddingNewSubCategory && subCategory && catalogHasValue(existingSubCategories, subCategory)) {
    errors.subCategory = "This sub category already exists. Select it from the list instead.";
  }
  if (
    isAddingNewUnitOfMeasure &&
    unitOfMeasure &&
    catalogHasValue(existingUnitsOfMeasure, unitOfMeasure)
  ) {
    errors.unitOfMeasure = "This unit of measure already exists. Select it from the list instead.";
  }
  if (!serviceCode) {
    errors.serviceCode = "Service code is required.";
  }
  if (!description) {
    errors.description = "Description is required.";
  }
  const { error: rateError } = parseOfferCostSheetRate(form.costSheetRate);
  if (rateError) {
    errors.costSheetRate = rateError;
  }
  if (requireMapping || hasAnyServiceCodeMappingValues(form)) {
    if (!String(form.sourceTable ?? "").trim()) {
      errors.sourceTable = "Source table is required.";
    }
    if (!String(form.fieldName ?? "").trim()) {
      errors.fieldName = "Field name is required.";
    }
    const seenFieldNames = new Set();
    let previousBlank = false;
    for (const key of MAPPING_FIELD_KEYS) {
      const value = String(form[key] ?? "").trim();
      if (!value) {
        previousBlank = true;
        continue;
      }
      if (previousBlank) {
        errors[key] = `Select ${MAPPING_FIELD_LABELS[previousMappingFieldKey(key)]} first.`;
      }
      if (seenFieldNames.has(value)) {
        errors[key] = "This column is already selected.";
      }
      seenFieldNames.add(value);
    }
  }
  return errors;
}

function formatOfferSaveError(err) {
  const data = err?.data;
  if (data?.errors && typeof data.errors === "object") {
    return data.errors;
  }
  return {
    _global:
      data?.detail ||
      data?.title ||
      (typeof data === "string" ? data : null) ||
      "Unable to save offer. Please try again.",
  };
}

/** Same triangle-dot spinner as the Clients page (see ClientsLoadingDots there). */
function ServiceCatalogLoadingDots() {
  return (
    <span className="ml-1 inline-flex align-middle" aria-hidden="true">
      <span className="relative inline-block h-3.5 w-3.5 animate-spin text-current [animation-duration:1.15s]">
        <span className="absolute left-1/2 top-0 block h-1 w-1 -translate-x-1/2 rounded-full bg-current" />
        <span className="absolute bottom-0 left-0 block h-1 w-1 rounded-full bg-current" />
        <span className="absolute bottom-0 right-0 block h-1 w-1 rounded-full bg-current" />
      </span>
    </span>
  );
}

function mapServiceToOfferForm(service) {
  const rawCategory = String(service.category ?? "").trim();
  const rate = service.costSheetRate;
  return {
    category: rawCategory === "Uncategorized" ? "" : rawCategory,
    subCategory: String(service.subCategory ?? "").trim(),
    serviceCode: String(service.serviceCode ?? "").trim(),
    description: String(service.description ?? "").trim(),
    glCode: String(service.glCode ?? "").trim(),
    unitOfMeasure: String(service.unitOfMeasure ?? "").trim(),
    costSheetRate:
      rate != null && !Number.isNaN(Number(rate)) ? Number(rate).toFixed(2) : "",
    sourceTable: "",
    fieldName: "",
    fieldName2: "",
    fieldName3: "",
    fieldName4: "",
    fieldName5: "",
  };
}

function mapApiRowToService(row, index) {
  const category = String(row.category ?? "").trim() || "Uncategorized";
  const serviceCode = String(row.serviceCode ?? "").trim() || `_row_${index}`;
  const description = String(row.description ?? "").trim();
  const rateRaw = row.costSheetRate;
  const costSheetRate =
    rateRaw != null && !Number.isNaN(Number(rateRaw)) ? Number(rateRaw) : 0;
  return {
    id: serviceCode,
    category,
    subCategory: String(row.subCategory ?? "").trim(),
    serviceCode,
    serviceName: description,
    description,
    unitOfMeasure: String(row.unitOfMeasure ?? "").trim(),
    glCode: String(row.glCode ?? "").trim(),
    costSheetRate,
    active: row.active === true,
  };
}

function ServiceCatalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const decodedToken = useAppSelector(selectAuth).decodedToken;
  const canAddOffer = hasOfferCatalogAddServiceButtonPrivilege(decodedToken);
  const { data, isLoading, isError, error, refetch } = useGetServiceCatalogQuery();
  const {
    data: codeMappingsData,
    isLoading: isCodeMappingsLoading,
    isError: isCodeMappingsError,
  } = useGetServiceCodeMappingsQuery();
  const {
    data: mappingOptionsData,
    isLoading: isMappingOptionsLoading,
  } = useGetServiceCodeMappingOptionsQuery();
  const [createOffer, { isLoading: isCreatingOffer }] = useCreateServiceCatalogOfferMutation();
  const [updateOffer, { isLoading: isUpdatingOffer }] = useUpdateServiceCatalogOfferMutation();
  const [serviceCodes, setServiceCodes] = useState([]);
  const [expandedServiceCode, setExpandedServiceCode] = useState(null);
  const [showOfferPanel, setShowOfferPanel] = useState(false);
  const [editingServiceCode, setEditingServiceCode] = useState(null);
  const [offerForm, setOfferForm] = useState(INITIAL_OFFER_FORM);
  const [offerFieldErrors, setOfferFieldErrors] = useState({});
  const [offerSaveSuccessBanner, setOfferSaveSuccessBanner] = useState(null);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [isAddingNewSubCategory, setIsAddingNewSubCategory] = useState(false);
  const [isAddingNewUnitOfMeasure, setIsAddingNewUnitOfMeasure] = useState(false);
  const selectedSourceTable = String(offerForm.sourceTable ?? "").trim();
  const {
    data: fieldNameColumns,
    isFetching: isFieldNamesLoading,
  } = useGetServiceCodeMappingColumnsQuery(selectedSourceTable, {
    skip: !showOfferPanel || Boolean(editingServiceCode) || !selectedSourceTable,
  });

  useEffect(() => {
    if (Array.isArray(data)) {
      setServiceCodes(data.map((row, i) => mapApiRowToService(row, i)));
    }
  }, [data]);

  const codeMappingsByServiceCode = useMemo(() => {
    const map = new Map();
    for (const row of codeMappingsData ?? []) {
      const key = String(row.catalogServiceCode ?? row.catalog_service_code ?? "").trim();
      if (!key) {
        continue;
      }
      const entry = {
        sourceTable: String(row.sourceTable ?? row.source_table ?? "").trim(),
        serviceCode: String(row.serviceCode ?? row.service_code ?? "").trim(),
        fieldName: String(row.fieldName ?? row.field_name ?? "").trim(),
        fieldName2: String(row.fieldName2 ?? row.field_name2 ?? "").trim(),
        fieldName3: String(row.fieldName3 ?? row.field_name3 ?? "").trim(),
        fieldName4: String(row.fieldName4 ?? row.field_name4 ?? "").trim(),
        fieldName5: String(row.fieldName5 ?? row.field_name5 ?? "").trim(),
        uom: String(row.uom ?? "").trim(),
      };
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(entry);
    }
    return map;
  }, [codeMappingsData]);

  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") ?? "").trim()
  );
  const [filterStatus, setFilterStatus] = useState(
    () => searchParams.get("status") ?? "active"
  );
  const [filterCategory, setFilterCategory] = useState(
    () => searchParams.get("category") ?? "all"
  );
  const [filterSubCategory, setFilterSubCategory] = useState(
    () => searchParams.get("subCategory") ?? "all"
  );
  const [page, setPage] = useState(() => {
    const p = Number.parseInt(searchParams.get("page") ?? "1", 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const q = debouncedSearch.trim();
        if (q) {
          next.set("search", q);
        } else {
          next.delete("search");
        }
        if (filterStatus && filterStatus !== "active") {
          next.set("status", filterStatus);
        } else {
          next.delete("status");
        }
        if (filterCategory && filterCategory !== "all") {
          next.set("category", filterCategory);
        } else {
          next.delete("category");
        }
        if (filterSubCategory && filterSubCategory !== "all") {
          next.set("subCategory", filterSubCategory);
        } else {
          next.delete("subCategory");
        }
        if (page > 1) {
          next.set("page", String(page));
        } else {
          next.delete("page");
        }
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearch, filterStatus, filterCategory, filterSubCategory, page, setSearchParams]);

  const activateService = (id) => {
    setServiceCodes((prev) =>
      prev.map((service) => (service.id === id ? { ...service, active: true } : service))
    );
  };

  const categories = Array.from(new Set(serviceCodes.map((s) => s.category))).sort();

  const catalogCategoryOptions = useMemo(() => {
    const set = new Set();
    for (const service of serviceCodes) {
      const category = String(service.category ?? "").trim();
      if (category) {
        set.add(category);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCodes]);

  const offerCategoryOptions = useMemo(() => {
    const set = new Set(catalogCategoryOptions);
    const current = String(offerForm.category ?? "").trim();
    if (current) {
      set.add(current);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [catalogCategoryOptions, offerForm.category]);

  const catalogSubCategoryOptions = useMemo(() => {
    const set = new Set();
    for (const service of serviceCodes) {
      const sub = String(service.subCategory ?? "").trim();
      if (sub) {
        set.add(sub);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCodes]);

  const offerSubCategoryOptions = useMemo(() => {
    const set = new Set(catalogSubCategoryOptions);
    const current = String(offerForm.subCategory ?? "").trim();
    if (current) {
      set.add(current);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [catalogSubCategoryOptions, offerForm.subCategory]);

  const showCategoryInput =
    isAddingNewCategory || (!isLoading && catalogCategoryOptions.length === 0);
  const showSubCategoryInput =
    isAddingNewSubCategory || (!isLoading && catalogSubCategoryOptions.length === 0);

  const catalogUnitOfMeasureOptions = useMemo(() => {
    const set = new Set();
    for (const service of serviceCodes) {
      const uom = String(service.unitOfMeasure ?? "").trim();
      if (uom) {
        set.add(uom);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCodes]);

  const unitOfMeasureOptions = useMemo(() => {
    const set = new Set(catalogUnitOfMeasureOptions);
    const current = String(offerForm.unitOfMeasure ?? "").trim();
    if (current) {
      set.add(current);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [catalogUnitOfMeasureOptions, offerForm.unitOfMeasure]);

  const showUnitOfMeasureInput =
    isAddingNewUnitOfMeasure || (!isLoading && catalogUnitOfMeasureOptions.length === 0);

  const subCategories = useMemo(() => {
    const set = new Set();
    for (const service of serviceCodes) {
      if (filterCategory !== "all" && service.category !== filterCategory) {
        continue;
      }
      const sub = String(service.subCategory ?? "").trim();
      if (sub) {
        set.add(sub);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCodes, filterCategory]);

  const sourceTableOptions = mappingOptionsData?.sourceTables ?? [];
  const fieldNameOptions = fieldNameColumns ?? [];

  const q = debouncedSearch.toLowerCase();
  const filteredServices = useMemo(() => {
    return serviceCodes.filter((service) => {
      const hay = [
        service.category,
        service.subCategory,
        service.serviceCode,
        service.serviceName,
        service.description,
        service.glCode,
        service.unitOfMeasure,
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      const matchesSearch = !q || hay.includes(q);
      const matchesStatus =
        filterStatus === "all"
          ? true
          : filterStatus === "active"
            ? service.active
            : !service.active;
      const matchesCategory = filterCategory === "all" ? true : service.category === filterCategory;
      const matchesSubCategory =
        filterSubCategory === "all" ? true : service.subCategory === filterSubCategory;
      return matchesSearch && matchesStatus && matchesCategory && matchesSubCategory;
    });
  }, [serviceCodes, q, filterStatus, filterCategory, filterSubCategory]);

  const totalServices = serviceCodes.length;
  const totalFiltered = filteredServices.length;
  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE) || 0;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredServices.slice(start, start + PAGE_SIZE);
  }, [filteredServices, page]);

  useEffect(() => {
    setPage(1);
    setExpandedServiceCode(null);
  }, [debouncedSearch, filterStatus, filterCategory, filterSubCategory]);

  useEffect(() => {
    if (filterSubCategory !== "all" && !subCategories.includes(filterSubCategory)) {
      setFilterSubCategory("all");
    }
  }, [filterSubCategory, subCategories]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const rangeStart = totalFiltered === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(page * PAGE_SIZE, totalFiltered);

  const loadErrorMessage =
    error?.data?.detail ||
    error?.data?.title ||
    error?.error ||
    (typeof error?.status === "number" ? `Unable to load services (HTTP ${error.status}).` : null) ||
    "Unable to load service catalog.";

  const resetOfferCategoryEntryMode = () => {
    setIsAddingNewCategory(false);
    setIsAddingNewSubCategory(false);
    setIsAddingNewUnitOfMeasure(false);
  };

  const handleOpenAddOfferPanel = () => {
    if (!canAddOffer) {
      return;
    }
    setEditingServiceCode(null);
    setOfferForm(INITIAL_OFFER_FORM);
    setOfferFieldErrors({});
    setOfferSaveSuccessBanner(null);
    resetOfferCategoryEntryMode();
    setShowOfferPanel(true);
  };

  const handleOpenEditOfferPanel = (service) => {
    setEditingServiceCode(service.serviceCode);
    setOfferForm(mapServiceToOfferForm(service));
    setOfferFieldErrors({});
    setOfferSaveSuccessBanner(null);
    resetOfferCategoryEntryMode();
    setShowOfferPanel(true);
  };

  const handleCloseOfferPanel = () => {
    setShowOfferPanel(false);
    setEditingServiceCode(null);
    setOfferForm(INITIAL_OFFER_FORM);
    setOfferFieldErrors({});
    setOfferSaveSuccessBanner(null);
    resetOfferCategoryEntryMode();
  };

  const handleStartAddNewCategory = () => {
    setIsAddingNewCategory(true);
    setOfferForm((prev) => ({ ...prev, category: "" }));
    setOfferFieldErrors((prev) => {
      if (!prev.category) {
        return prev;
      }
      const next = { ...prev };
      delete next.category;
      return next;
    });
  };

  const handleUseExistingCategory = () => {
    setIsAddingNewCategory(false);
    setOfferForm((prev) => ({ ...prev, category: "" }));
    setOfferFieldErrors((prev) => {
      if (!prev.category) {
        return prev;
      }
      const next = { ...prev };
      delete next.category;
      return next;
    });
  };

  const handleStartAddNewSubCategory = () => {
    setIsAddingNewSubCategory(true);
    setOfferForm((prev) => ({ ...prev, subCategory: "" }));
    setOfferFieldErrors((prev) => {
      if (!prev.subCategory) {
        return prev;
      }
      const next = { ...prev };
      delete next.subCategory;
      return next;
    });
  };

  const handleUseExistingSubCategory = () => {
    setIsAddingNewSubCategory(false);
    setOfferForm((prev) => ({ ...prev, subCategory: "" }));
    setOfferFieldErrors((prev) => {
      if (!prev.subCategory) {
        return prev;
      }
      const next = { ...prev };
      delete next.subCategory;
      return next;
    });
  };

  const handleStartAddNewUnitOfMeasure = () => {
    setIsAddingNewUnitOfMeasure(true);
    setOfferForm((prev) => ({ ...prev, unitOfMeasure: "" }));
    setOfferFieldErrors((prev) => {
      if (!prev.unitOfMeasure) {
        return prev;
      }
      const next = { ...prev };
      delete next.unitOfMeasure;
      return next;
    });
  };

  const handleUseExistingUnitOfMeasure = () => {
    setIsAddingNewUnitOfMeasure(false);
    setOfferForm((prev) => ({ ...prev, unitOfMeasure: "" }));
    setOfferFieldErrors((prev) => {
      if (!prev.unitOfMeasure) {
        return prev;
      }
      const next = { ...prev };
      delete next.unitOfMeasure;
      return next;
    });
  };

  const handleOfferFieldChange = (field, value) => {
    setOfferForm((prev) => ({ ...prev, [field]: value }));
    setOfferFieldErrors((prev) => {
      const keysToClear = [field];
      if (field === "category") {
        keysToClear.push("sourceTable", ...MAPPING_FIELD_KEYS);
      }
      if (!keysToClear.some((key) => prev[key])) {
        return prev;
      }
      const next = { ...prev };
      for (const key of keysToClear) {
        delete next[key];
      }
      return next;
    });
  };

  const handleMappingFieldChange = (field, value) => {
    setOfferForm((prev) => {
      const next = { ...prev, [field]: value };
      const index = MAPPING_FIELD_KEYS.indexOf(field);
      if (index >= 0) {
        for (let i = index + 1; i < MAPPING_FIELD_KEYS.length; i += 1) {
          next[MAPPING_FIELD_KEYS[i]] = "";
        }
      }
      return next;
    });
    setOfferFieldErrors((prev) => {
      const keysToClear = [field];
      const index = MAPPING_FIELD_KEYS.indexOf(field);
      if (index >= 0) {
        keysToClear.push(...MAPPING_FIELD_KEYS.slice(index + 1));
      }
      if (!keysToClear.some((key) => prev[key])) {
        return prev;
      }
      const next = { ...prev };
      for (const key of keysToClear) {
        delete next[key];
      }
      return next;
    });
  };

  const handleSaveOffer = async () => {
    setOfferSaveSuccessBanner(null);
    const clientErrors = validateOfferForm(offerForm, {
      requireMapping: !editingServiceCode && !isFlatFeeCategory(offerForm.category),
      isAddingNewCategory: showCategoryInput,
      isAddingNewSubCategory: showSubCategoryInput,
      isAddingNewUnitOfMeasure: showUnitOfMeasureInput,
      existingCategories: catalogCategoryOptions,
      existingSubCategories: catalogSubCategoryOptions,
      existingUnitsOfMeasure: catalogUnitOfMeasureOptions,
    });
    if (Object.keys(clientErrors).length > 0) {
      setOfferFieldErrors(clientErrors);
      return;
    }
    const { value: costSheetRate } = parseOfferCostSheetRate(offerForm.costSheetRate);
    const payload = {
      category: offerForm.category.trim(),
      subCategory: offerForm.subCategory.trim(),
      serviceCode: offerForm.serviceCode.trim(),
      description: offerForm.description.trim(),
      glCode: offerForm.glCode.trim(),
      unitOfMeasure: offerForm.unitOfMeasure.trim(),
      costSheetRate,
    };
    if (!editingServiceCode) {
      payload.sourceTable = offerForm.sourceTable.trim();
      payload.fieldName = offerForm.fieldName.trim();
      payload.fieldName2 = offerForm.fieldName2.trim();
      payload.fieldName3 = offerForm.fieldName3.trim();
      payload.fieldName4 = offerForm.fieldName4.trim();
      payload.fieldName5 = offerForm.fieldName5.trim();
    }
    try {
      if (editingServiceCode) {
        await updateOffer({
          serviceCode: editingServiceCode,
          body: payload,
        }).unwrap();
        handleCloseOfferPanel();
        return;
      }
      await createOffer(payload).unwrap();
      setOfferSaveSuccessBanner("Offer saved successfully.");
      setOfferForm(INITIAL_OFFER_FORM);
      setOfferFieldErrors({});
      resetOfferCategoryEntryMode();
    } catch (err) {
      setOfferFieldErrors(formatOfferSaveError(err));
    }
  };

  const isSavingOffer = isCreatingOffer || isUpdatingOffer;

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Offer Catalog
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage billable service codes used in contracts, activity tracking, and invoices
        </p>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search service codes, names, categories, GL code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={isLoading || isError}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setFilterSubCategory("all");
            }}
            disabled={isLoading || isError || serviceCodes.length === 0}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <select
            value={filterSubCategory}
            onChange={(e) => setFilterSubCategory(e.target.value)}
            disabled={isLoading || isError || serviceCodes.length === 0 || subCategories.length === 0}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          >
            <option value="all">All Sub Categories</option>
            {subCategories.map((subCategory) => (
              <option key={subCategory} value={subCategory}>
                {subCategory}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            disabled={isLoading || isError}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          >
            <option value="all">All Services</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
        {canAddOffer ? (
          <button
            type="button"
            className="app-btn-gloss ml-4"
            onClick={handleOpenAddOfferPanel}
          >
            <Plus className="w-5 h-5" />
            Add Offer
          </button>
        ) : null}
      </div>

      {isError ? (
        <div className="rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-200 mb-6">
          <p className="font-medium">Could not load catalog</p>
          <p className="mt-1">{loadErrorMessage}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="app-btn-gloss mt-3 px-3 py-1.5 text-sm"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!isError ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-3 w-10" aria-label="Expand row" />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Sub Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Service Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    GL Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Unit of Measure
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Cost Sheet Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-8 text-center text-sm font-bold text-red-600 dark:text-red-400"
                    >
                      <span className="inline-flex items-center justify-center gap-0.5">
                        <span>Loading catalog</span>
                        <ServiceCatalogLoadingDots />
                      </span>
                    </td>
                  </tr>
                ) : serviceCodes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      No services found in <code className="text-sm">hgappact.act_services</code>.
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((service) => {
                    const serviceMappings = codeMappingsByServiceCode.get(service.serviceCode) ?? [];
                    const visibleOptionalFieldColumns =
                      getVisibleServiceCodeMappingOptionalColumns(serviceMappings);
                    const isExpanded = expandedServiceCode === service.serviceCode;
                    return (
                      <Fragment key={service.id}>
                        <tr
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            !service.active ? "opacity-50 bg-gray-50 dark:bg-gray-800" : ""
                          }`}
                        >
                          <td className="px-3 py-4 text-sm">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedServiceCode(isExpanded ? null : service.serviceCode)
                              }
                              className="app-btn-plain p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded
                                  ? "Collapse service code mappings"
                                  : "Expand service code mappings"
                              }
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {service.category}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                            {service.subCategory}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {service.serviceCode}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            {service.description}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                            {service.glCode}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                            {service.unitOfMeasure}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                            ${service.costSheetRate.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                service.active
                                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                  : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              }`}
                            >
                              {service.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleOpenEditOfferPanel(service)}
                                className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                aria-label={`Edit offer ${service.serviceCode}`}
                              >
                                <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </button>
                              {!service.active ? (
                                <button
                                  type="button"
                                  onClick={() => activateService(service.id)}
                                  className="app-btn-plain px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                >
                                  Activate
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>

                        {isExpanded ? (
                          <tr className="bg-gray-50 dark:bg-gray-800">
                            <td colSpan={10} className="px-6 py-4">
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                Service Code Mappings
                              </h4>
                              {isCodeMappingsLoading ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                  Loading mappings…
                                </p>
                              ) : isCodeMappingsError ? (
                                <p className="text-xs text-red-600 dark:text-red-400 italic">
                                  Could not load service code mappings.
                                </p>
                              ) : serviceMappings.length === 0 ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                  No mappings in{" "}
                                  <code className="text-xs">hgappact.act_service_code_mapping</code>
                                </p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-left text-gray-700 dark:text-gray-300">
                                        <th className="pb-2 pr-4 font-medium">Source Table</th>
                                        <th className="pb-2 pr-4 font-medium">Service Code</th>
                                        <th className="pb-2 pr-4 font-medium">Field Name</th>
                                        {visibleOptionalFieldColumns.map(({ key, label }) => (
                                          <th key={key} className="pb-2 pr-4 font-medium">
                                            {label}
                                          </th>
                                        ))}
                                        <th className="pb-2 font-medium">UOM</th>
                                      </tr>
                                    </thead>
                                    <tbody className="text-gray-900 dark:text-gray-100">
                                      {serviceMappings.map((mapping, index) => (
                                        <tr
                                          key={`${mapping.sourceTable}-${mapping.serviceCode}-${mapping.fieldName}-${mapping.fieldName2}-${mapping.fieldName3}-${mapping.fieldName4}-${mapping.fieldName5}-${index}`}
                                        >
                                          <td className="py-1 pr-4 font-mono">
                                            {mapping.sourceTable || "—"}
                                          </td>
                                          <td className="py-1 pr-4 font-mono">
                                            {mapping.serviceCode || "—"}
                                          </td>
                                          <td className="py-1 pr-4 font-mono">
                                            {mapping.fieldName || "—"}
                                          </td>
                                          {visibleOptionalFieldColumns.map(({ key }) => (
                                            <td key={key} className="py-1 pr-4 font-mono">
                                              {mapping[key] || "—"}
                                            </td>
                                          ))}
                                          <td className="py-1">{mapping.uom || "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
                {!isLoading && serviceCodes.length > 0 && totalFiltered === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      No services match your filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {!isLoading && totalFiltered > 0 ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-medium text-gray-900 dark:text-gray-100">{rangeStart}</span>
                –<span className="font-medium text-gray-900 dark:text-gray-100">{rangeEnd}</span> of{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">{totalFiltered}</span>
                {totalFiltered !== totalServices ? (
                  <span className="text-gray-500 dark:text-gray-500"> (filtered)</span>
                ) : null}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setExpandedServiceCode(null);
                    setPage((p) => Math.max(1, p - 1));
                  }}
                  disabled={page <= 1}
                  className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 px-2 tabular-nums">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setExpandedServiceCode(null);
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                  disabled={page >= totalPages}
                  className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {showOfferPanel ? (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCloseOfferPanel}
            role="presentation"
          />

          <div className="fixed inset-y-0 right-0 flex w-full max-w-2xl">
            <div className="relative flex w-full flex-col bg-white shadow-xl dark:bg-gray-900">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingServiceCode ? "Edit Offer" : "Add Offer"}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseOfferPanel}
                  className="app-btn-plain rounded p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  aria-label="Close offer panel"
                >
                  <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {offerSaveSuccessBanner ? (
                    <div
                      className="flex items-start justify-between gap-3 rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-100"
                      role="status"
                    >
                      <p className="font-medium">{offerSaveSuccessBanner}</p>
                      <button
                        type="button"
                        onClick={() => setOfferSaveSuccessBanner(null)}
                        className="app-btn-plain shrink-0 rounded p-1 text-green-800 hover:bg-green-100 dark:text-green-200 dark:hover:bg-green-900/50"
                        aria-label="Dismiss success message"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}

                  {offerFieldErrors._global ? (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {offerFieldErrors._global}
                    </p>
                  ) : null}

                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {editingServiceCode
                      ? "Update this offer in "
                      : "New offers are saved to "}
                    <code className="text-sm">hgappact.act_services</code>
                    {editingServiceCode ? (
                      ". Service code cannot be changed."
                    ) : (
                      <>
                        {" "}
                        and <code className="text-sm">hgappact.act_service_code_mapping</code>
                        {" "}
                        and appear in the catalog when active.
                      </>
                    )}
                  </p>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label
                          htmlFor="offer-category"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Category *
                        </label>
                        {!showCategoryInput ? (
                          <button
                            type="button"
                            onClick={handleStartAddNewCategory}
                            className={offerAddValueToggleClassName()}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add new
                          </button>
                        ) : null}
                        {showCategoryInput && catalogCategoryOptions.length > 0 ? (
                          <button
                            type="button"
                            onClick={handleUseExistingCategory}
                            className={offerAddValueToggleClassName()}
                          >
                            Select existing
                          </button>
                        ) : null}
                      </div>
                      {showCategoryInput ? (
                        <input
                          id="offer-category"
                          type="text"
                          value={offerForm.category}
                          onChange={(e) => handleOfferFieldChange("category", e.target.value)}
                          disabled={isLoading}
                          aria-invalid={Boolean(offerFieldErrors.category)}
                          autoComplete="off"
                          placeholder="Enter new category"
                          className={`${offerFieldClassName(Boolean(offerFieldErrors.category))} disabled:opacity-60`}
                        />
                      ) : (
                        <select
                          id="offer-category"
                          value={offerForm.category}
                          onChange={(e) => handleOfferFieldChange("category", e.target.value)}
                          disabled={isLoading || offerCategoryOptions.length === 0}
                          aria-invalid={Boolean(offerFieldErrors.category)}
                          className={`${offerFieldClassName(Boolean(offerFieldErrors.category))} disabled:opacity-60`}
                        >
                          <option value="">
                            {isLoading
                              ? "Loading categories…"
                              : offerCategoryOptions.length === 0
                                ? "No categories in catalog"
                                : "Select category"}
                          </option>
                          {offerCategoryOptions.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      )}
                      {offerFieldErrors.category ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {offerFieldErrors.category}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label
                          htmlFor="offer-sub-category"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Sub Category
                        </label>
                        {!showSubCategoryInput ? (
                          <button
                            type="button"
                            onClick={handleStartAddNewSubCategory}
                            className={offerAddValueToggleClassName()}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add new
                          </button>
                        ) : null}
                        {showSubCategoryInput && catalogSubCategoryOptions.length > 0 ? (
                          <button
                            type="button"
                            onClick={handleUseExistingSubCategory}
                            className={offerAddValueToggleClassName()}
                          >
                            Select existing
                          </button>
                        ) : null}
                      </div>
                      {showSubCategoryInput ? (
                        <input
                          id="offer-sub-category"
                          type="text"
                          value={offerForm.subCategory}
                          onChange={(e) => handleOfferFieldChange("subCategory", e.target.value)}
                          disabled={isLoading}
                          autoComplete="off"
                          placeholder="Enter new sub category"
                          className={`${offerFieldClassName(Boolean(offerFieldErrors.subCategory))} disabled:opacity-60`}
                        />
                      ) : (
                        <select
                          id="offer-sub-category"
                          value={offerForm.subCategory}
                          onChange={(e) => handleOfferFieldChange("subCategory", e.target.value)}
                          disabled={isLoading || offerSubCategoryOptions.length === 0}
                          aria-invalid={Boolean(offerFieldErrors.subCategory)}
                          className={`${offerFieldClassName(Boolean(offerFieldErrors.subCategory))} disabled:opacity-60`}
                        >
                          <option value="">
                            {isLoading
                              ? "Loading sub categories…"
                              : offerSubCategoryOptions.length === 0
                                ? "No sub categories in catalog"
                                : "Select sub category"}
                          </option>
                          {offerSubCategoryOptions.map((subCategory) => (
                            <option key={subCategory} value={subCategory}>
                              {subCategory}
                            </option>
                          ))}
                        </select>
                      )}
                      {offerFieldErrors.subCategory ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {offerFieldErrors.subCategory}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="offer-service-code"
                      className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Service Code *
                    </label>
                    <input
                      id="offer-service-code"
                      type="text"
                      value={offerForm.serviceCode}
                      onChange={(e) => handleOfferFieldChange("serviceCode", e.target.value)}
                      readOnly={Boolean(editingServiceCode)}
                      aria-readonly={editingServiceCode ? "true" : undefined}
                      aria-invalid={Boolean(offerFieldErrors.serviceCode)}
                      className={`${offerFieldClassName(Boolean(offerFieldErrors.serviceCode))}${
                        editingServiceCode
                          ? " bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                          : ""
                      }`}
                      placeholder="Unique service code"
                    />
                    {offerFieldErrors.serviceCode ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {offerFieldErrors.serviceCode}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="offer-description"
                      className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Description *
                    </label>
                    <textarea
                      id="offer-description"
                      rows={3}
                      value={offerForm.description}
                      onChange={(e) => handleOfferFieldChange("description", e.target.value)}
                      aria-invalid={Boolean(offerFieldErrors.description)}
                      className={offerFieldClassName(Boolean(offerFieldErrors.description))}
                      placeholder="Describe the billable service"
                    />
                    {offerFieldErrors.description ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {offerFieldErrors.description}
                      </p>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="offer-gl-code"
                        className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        GL Code
                      </label>
                      <input
                        id="offer-gl-code"
                        type="text"
                        value={offerForm.glCode}
                        onChange={(e) => handleOfferFieldChange("glCode", e.target.value)}
                        className={offerFieldClassName(false)}
                        placeholder="General ledger code"
                      />
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label
                          htmlFor="offer-unit-of-measure"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Unit of Measure
                        </label>
                        {!showUnitOfMeasureInput ? (
                          <button
                            type="button"
                            onClick={handleStartAddNewUnitOfMeasure}
                            className={offerAddValueToggleClassName()}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add new
                          </button>
                        ) : null}
                        {showUnitOfMeasureInput && catalogUnitOfMeasureOptions.length > 0 ? (
                          <button
                            type="button"
                            onClick={handleUseExistingUnitOfMeasure}
                            className={offerAddValueToggleClassName()}
                          >
                            Select existing
                          </button>
                        ) : null}
                      </div>
                      {showUnitOfMeasureInput ? (
                        <input
                          id="offer-unit-of-measure"
                          type="text"
                          value={offerForm.unitOfMeasure}
                          onChange={(e) => handleOfferFieldChange("unitOfMeasure", e.target.value)}
                          disabled={isLoading}
                          aria-invalid={Boolean(offerFieldErrors.unitOfMeasure)}
                          autoComplete="off"
                          placeholder="Enter new unit of measure"
                          className={`${offerFieldClassName(Boolean(offerFieldErrors.unitOfMeasure))} disabled:opacity-60`}
                        />
                      ) : (
                        <select
                          id="offer-unit-of-measure"
                          value={offerForm.unitOfMeasure}
                          onChange={(e) => handleOfferFieldChange("unitOfMeasure", e.target.value)}
                          disabled={isLoading || unitOfMeasureOptions.length === 0}
                          aria-invalid={Boolean(offerFieldErrors.unitOfMeasure)}
                          className={`${offerFieldClassName(Boolean(offerFieldErrors.unitOfMeasure))} disabled:opacity-60`}
                        >
                          <option value="">
                            {isLoading
                              ? "Loading units of measure…"
                              : unitOfMeasureOptions.length === 0
                                ? "No units of measure in catalog"
                                : "Select unit of measure"}
                          </option>
                          {unitOfMeasureOptions.map((uom) => (
                            <option key={uom} value={uom}>
                              {uom}
                            </option>
                          ))}
                        </select>
                      )}
                      {offerFieldErrors.unitOfMeasure ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {offerFieldErrors.unitOfMeasure}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="offer-cost-sheet-rate"
                      className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Cost Sheet Rate
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                        $
                      </span>
                      <input
                        id="offer-cost-sheet-rate"
                        type="text"
                        inputMode="decimal"
                        value={offerForm.costSheetRate}
                        onChange={(e) => handleOfferFieldChange("costSheetRate", e.target.value)}
                        aria-invalid={Boolean(offerFieldErrors.costSheetRate)}
                        className={`${offerFieldClassName(Boolean(offerFieldErrors.costSheetRate))} pl-7`}
                        placeholder="0.00"
                      />
                    </div>
                    {offerFieldErrors.costSheetRate ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {offerFieldErrors.costSheetRate}
                      </p>
                    ) : null}
                  </div>

                  {!editingServiceCode ? (
                    <div className="space-y-4 border-t border-gray-200 pt-6 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Service Code Mapping
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Service code and unit of measure are copied from this offer.
                        {isFlatFeeCategory(offerForm.category)
                          ? " Source table and Field Name are optional for Flat Fee. If you add a mapping, Field Name is required after Source Table."
                          : " Source table and Field Name are required."}{" "}
                        Additional field names are optional and must be selected in order. A column
                        cannot be used twice.
                      </p>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="offer-source-table"
                            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Source Table
                            {!isFlatFeeCategory(offerForm.category) ? " *" : ""}
                          </label>
                          <select
                            id="offer-source-table"
                            value={offerForm.sourceTable}
                            onChange={(e) => {
                              const sourceTable = e.target.value;
                              setOfferForm((prev) => ({
                                ...prev,
                                sourceTable,
                                fieldName: "",
                                fieldName2: "",
                                fieldName3: "",
                                fieldName4: "",
                                fieldName5: "",
                              }));
                              setOfferFieldErrors((prev) => {
                                if (
                                  !prev.sourceTable &&
                                  !MAPPING_FIELD_KEYS.some((key) => prev[key])
                                ) {
                                  return prev;
                                }
                                const next = { ...prev };
                                delete next.sourceTable;
                                for (const key of MAPPING_FIELD_KEYS) {
                                  delete next[key];
                                }
                                return next;
                              });
                            }}
                            disabled={isMappingOptionsLoading || sourceTableOptions.length === 0}
                            aria-invalid={Boolean(offerFieldErrors.sourceTable)}
                            className={`${offerFieldClassName(Boolean(offerFieldErrors.sourceTable))} disabled:opacity-60`}
                          >
                            <option value="">
                              {isMappingOptionsLoading
                                ? "Loading source tables…"
                                : sourceTableOptions.length === 0
                                  ? "No tables in hgappact"
                                  : "Select source table"}
                            </option>
                            {sourceTableOptions.map((sourceTable) => (
                              <option key={sourceTable} value={sourceTable}>
                                {sourceTable}
                              </option>
                            ))}
                          </select>
                          {offerFieldErrors.sourceTable ? (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                              {offerFieldErrors.sourceTable}
                            </p>
                          ) : null}
                        </div>
                        <div>
                          <label
                            htmlFor="offer-field-name"
                            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Field Name
                            {!isFlatFeeCategory(offerForm.category) ? " *" : ""}
                          </label>
                          <select
                            id="offer-field-name"
                            value={offerForm.fieldName}
                            onChange={(e) => handleMappingFieldChange("fieldName", e.target.value)}
                            disabled={
                              !selectedSourceTable ||
                              isFieldNamesLoading ||
                              fieldNameOptions.length === 0
                            }
                            aria-invalid={Boolean(offerFieldErrors.fieldName)}
                            className={`${offerFieldClassName(Boolean(offerFieldErrors.fieldName))} disabled:opacity-60`}
                          >
                            <option value="">
                              {!selectedSourceTable
                                ? "Select a source table first"
                                : isFieldNamesLoading
                                  ? "Loading columns…"
                                  : fieldNameOptions.length === 0
                                    ? "No columns for this table"
                                    : "Select field name"}
                            </option>
                            {mappingFieldOptions(fieldNameOptions, offerForm, "fieldName").map(
                              (fieldName) => (
                                <option key={fieldName} value={fieldName}>
                                  {fieldName}
                                </option>
                              )
                            )}
                          </select>
                          {offerFieldErrors.fieldName ? (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                              {offerFieldErrors.fieldName}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {SERVICE_CODE_MAPPING_OPTIONAL_FIELD_COLUMNS.map(({ key, label }) => {
                          const previousKey = previousMappingFieldKey(key);
                          const previousSelected = Boolean(
                            String(offerForm[previousKey] ?? "").trim()
                          );
                          const options = mappingFieldOptions(fieldNameOptions, offerForm, key);
                          const enabled =
                            Boolean(selectedSourceTable) &&
                            !isFieldNamesLoading &&
                            previousSelected &&
                            options.length > 0;
                          let placeholder = "Optional";
                          if (!selectedSourceTable) {
                            placeholder = "Select a source table first";
                          } else if (isFieldNamesLoading) {
                            placeholder = "Loading columns…";
                          } else if (!previousSelected) {
                            placeholder = `Select ${MAPPING_FIELD_LABELS[previousKey]} first`;
                          } else if (options.length === 0) {
                            placeholder = "No remaining columns";
                          } else {
                            placeholder = `Select ${label.toLowerCase()}`;
                          }
                          return (
                            <div key={key}>
                              <label
                                htmlFor={`offer-${key}`}
                                className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                {label}
                              </label>
                              <select
                                id={`offer-${key}`}
                                value={offerForm[key]}
                                onChange={(e) => handleMappingFieldChange(key, e.target.value)}
                                disabled={!enabled}
                                aria-invalid={Boolean(offerFieldErrors[key])}
                                className={`${offerFieldClassName(Boolean(offerFieldErrors[key]))} disabled:opacity-60`}
                              >
                                <option value="">{placeholder}</option>
                                {options.map((fieldName) => (
                                  <option key={fieldName} value={fieldName}>
                                    {fieldName}
                                  </option>
                                ))}
                              </select>
                              {offerFieldErrors[key] ? (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                                  {offerFieldErrors[key]}
                                </p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleCloseOfferPanel}
                  className="app-btn-gloss-cancel px-6"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveOffer}
                  disabled={isSavingOffer}
                  className="app-btn-gloss px-6 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {isSavingOffer
                    ? "Saving…"
                    : editingServiceCode
                      ? "Update Offer"
                      : "Save Offer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { ServiceCatalog };
